import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  FileText, 
  Plus, 
  Search, 
  Eye,
  Send,
  Download,
  Edit3,
  CheckCircle,
  XCircle,
  Clock,
  Euro,
  Zap,
  Calendar,
  Users
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Devis = () => {
  const [devis, setDevis] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showAddForm, setShowAddForm] = useState(false);
  const [newDevis, setNewDevis] = useState({
    client_id: '',
    puissance_kwc: '',
    nombre_panneaux: '',
    prix_unitaire: '300',
    economies_annuelles: '',
    notes: ''
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [devisResponse, clientsResponse] = await Promise.all([
        axios.get(`${API}/devis`),
        axios.get(`${API}/clients`)
      ]);
      setDevis(devisResponse.data);
      setClients(clientsResponse.data);
    } catch (error) {
      console.error('Erreur lors du chargement des données:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const devisData = {
        ...newDevis,
        puissance_kwc: parseFloat(newDevis.puissance_kwc),
        nombre_panneaux: parseInt(newDevis.nombre_panneaux),
        prix_unitaire: parseFloat(newDevis.prix_unitaire),
        economies_annuelles: newDevis.economies_annuelles ? parseFloat(newDevis.economies_annuelles) : null
      };

      const response = await axios.post(`${API}/devis`, devisData);
      setDevis([response.data, ...devis]);
      setShowAddForm(false);
      setNewDevis({
        client_id: '',
        puissance_kwc: '',
        nombre_panneaux: '',
        prix_unitaire: '300',
        economies_annuelles: '',
        notes: ''
      });
      // Reload data to get updated client status
      loadData();
    } catch (error) {
      console.error('Erreur lors de la création du devis:', error);
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      brouillon: { label: 'Brouillon', class: 'badge-secondary', icon: Edit3 },
      envoye: { label: 'Envoyé', class: 'badge-warning', icon: Send },
      accepte: { label: 'Accepté', class: 'badge-success', icon: CheckCircle },
      refuse: { label: 'Refusé', class: 'badge-danger', icon: XCircle },
      expire: { label: 'Expiré', class: 'badge-secondary', icon: Clock }
    };
    return badges[status] || { label: status, class: 'badge-secondary', icon: FileText };
  };

  const getClientName = (clientId) => {
    const client = clients.find(c => c.id === clientId);
    return client ? `${client.prenom} ${client.nom}` : 'Client non trouvé';
  };

  const filteredDevis = devis.filter(d => {
    const clientName = getClientName(d.client_id);
    const matchesSearch = `${d.numero} ${clientName}`.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || d.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const getStatusCount = (status) => {
    return devis.filter(d => d.status === status).length;
  };

  const calculateEstimatedPower = () => {
    if (!newDevis.nombre_panneaux) return '';
    const puissance = parseInt(newDevis.nombre_panneaux) * 0.4; // 400W par panneau
    setNewDevis({...newDevis, puissance_kwc: puissance.toString()});
  };

  const calculateEstimatedSavings = () => {
    if (!newDevis.puissance_kwc) return '';
    const savings = parseFloat(newDevis.puissance_kwc) * 1200; // 1200€ par kWc par an
    setNewDevis({...newDevis, economies_annuelles: savings.toString()});
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 fade-in">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Devis & Factures
          </h1>
          <p className="text-gray-600">
            Gérez vos devis et factures panneaux solaires
          </p>
        </div>
        <div className="mt-4 lg:mt-0">
          <button 
            onClick={() => setShowAddForm(true)}
            className="btn btn-primary"
          >
            <Plus className="w-4 h-4" />
            Nouveau devis
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="stats-card">
          <div className="flex items-center justify-between">
            <div>
              <div className="stats-value">{devis.length}</div>
              <div className="stats-label">Total devis</div>
            </div>
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
              <FileText className="w-5 h-5 text-blue-600" />
            </div>
          </div>
        </div>
        <div className="stats-card">
          <div className="flex items-center justify-between">
            <div>
              <div className="stats-value">{getStatusCount('envoye')}</div>
              <div className="stats-label">En attente</div>
            </div>
            <div className="w-10 h-10 bg-yellow-100 rounded-lg flex items-center justify-center">
              <Clock className="w-5 h-5 text-yellow-600" />
            </div>
          </div>
        </div>
        <div className="stats-card">
          <div className="flex items-center justify-between">
            <div>
              <div className="stats-value">{getStatusCount('accepte')}</div>
              <div className="stats-label">Acceptés</div>
            </div>
            <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
              <CheckCircle className="w-5 h-5 text-green-600" />
            </div>
          </div>
        </div>
        <div className="stats-card">
          <div className="flex items-center justify-between">
            <div>
              <div className="stats-value">
                {new Intl.NumberFormat('fr-FR', { 
                  style: 'currency', 
                  currency: 'EUR',
                  maximumFractionDigits: 0
                }).format(devis.filter(d => d.status === 'accepte').reduce((sum, d) => sum + d.prix_ttc, 0))}
              </div>
              <div className="stats-label">CA signé</div>
            </div>
            <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
              <Euro className="w-5 h-5 text-purple-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="card">
        <div className="card-body">
          <div className="flex flex-col lg:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Rechercher par numéro, client..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="form-input pl-10"
                />
              </div>
            </div>
            <div className="lg:w-48">
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="form-select"
              >
                <option value="all">Tous les statuts</option>
                <option value="brouillon">Brouillons</option>
                <option value="envoye">Envoyés</option>
                <option value="accepte">Acceptés</option>
                <option value="refuse">Refusés</option>
                <option value="expire">Expirés</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Devis Table */}
      <div className="table-container">
        <table className="table">
          <thead>
            <tr>
              <th>Devis</th>
              <th>Client</th>
              <th>Installation</th>
              <th>Montant</th>
              <th>Statut</th>
              <th>Validité</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredDevis.map((devisItem) => {
              const statusBadge = getStatusBadge(devisItem.status);
              const StatusIcon = statusBadge.icon;
              
              return (
                <tr key={devisItem.id}>
                  <td>
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-gradient-to-br from-secondary/20 to-secondary/5 rounded-full flex items-center justify-center">
                        <FileText className="w-5 h-5 text-secondary" />
                      </div>
                      <div>
                        <div className="font-medium text-gray-900">
                          {devisItem.numero}
                        </div>
                        <div className="text-sm text-gray-500">
                          {new Date(devisItem.date_creation).toLocaleDateString('fr-FR')}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td>
                    <div className="flex items-center gap-2">
                      <Users className="w-4 h-4 text-gray-400" />
                      <span className="font-medium">{getClientName(devisItem.client_id)}</span>
                    </div>
                  </td>
                  <td>
                    <div className="space-y-1">
                      <div className="flex items-center gap-2 text-sm">
                        <Zap className="w-3 h-3 text-gray-400" />
                        <span>{devisItem.puissance_kwc} kWc</span>
                      </div>
                      <div className="flex items-center gap-2 text-sm text-gray-500">
                        <span>{devisItem.nombre_panneaux} panneaux</span>
                      </div>
                    </div>
                  </td>
                  <td>
                    <div>
                      <div className="font-semibold text-gray-900">
                        {new Intl.NumberFormat('fr-FR', { 
                          style: 'currency', 
                          currency: 'EUR'
                        }).format(devisItem.prix_ttc)}
                      </div>
                      <div className="text-sm text-gray-500">TTC</div>
                    </div>
                  </td>
                  <td>
                    <div className="flex items-center gap-2">
                      <StatusIcon className="w-4 h-4" />
                      <span className={`badge ${statusBadge.class}`}>
                        {statusBadge.label}
                      </span>
                    </div>
                  </td>
                  <td>
                    <div className="text-sm">
                      <div>Jusqu'au</div>
                      <div className="font-medium">
                        {new Date(devisItem.date_validite).toLocaleDateString('fr-FR')}
                      </div>
                    </div>
                  </td>
                  <td>
                    <div className="flex items-center gap-2">
                      <button className="btn btn-secondary btn-sm">
                        <Eye className="w-3 h-3" />
                      </button>
                      <button className="btn btn-secondary btn-sm">
                        <Download className="w-3 h-3" />
                      </button>
                      {devisItem.status === 'brouillon' && (
                        <button className="btn btn-primary btn-sm">
                          <Send className="w-3 h-3" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        
        {filteredDevis.length === 0 && (
          <div className="p-8 text-center text-gray-500">
            <FileText className="w-12 h-12 mx-auto mb-3 text-gray-300" />
            <p>Aucun devis trouvé</p>
          </div>
        )}
      </div>

      {/* Add Devis Modal */}
      {showAddForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-xl font-semibold text-gray-900">Nouveau Devis</h2>
            </div>
            
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div className="form-group">
                <label className="form-label">Client *</label>
                <select
                  value={newDevis.client_id}
                  onChange={(e) => setNewDevis({...newDevis, client_id: e.target.value})}
                  className="form-select"
                  required
                >
                  <option value="">Sélectionner un client</option>
                  {clients.map((client) => (
                    <option key={client.id} value={client.id}>
                      {client.prenom} {client.nom} - {client.ville}
                    </option>
                  ))}
                </select>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="form-group">
                  <label className="form-label">Nombre de panneaux *</label>
                  <input
                    type="number"
                    value={newDevis.nombre_panneaux}
                    onChange={(e) => {
                      setNewDevis({...newDevis, nombre_panneaux: e.target.value});
                      if (e.target.value) {
                        const puissance = parseInt(e.target.value) * 0.4;
                        setNewDevis(prev => ({...prev, nombre_panneaux: e.target.value, puissance_kwc: puissance.toString()}));
                      }
                    }}
                    className="form-input"
                    required
                  />
                  <p className="text-xs text-gray-500 mt-1">400W par panneau</p>
                </div>
                <div className="form-group">
                  <label className="form-label">Puissance (kWc) *</label>
                  <input
                    type="number"
                    step="0.1"
                    value={newDevis.puissance_kwc}
                    onChange={(e) => setNewDevis({...newDevis, puissance_kwc: e.target.value})}
                    className="form-input"
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="form-group">
                  <label className="form-label">Prix unitaire (€/panneau) *</label>
                  <input
                    type="number"
                    value={newDevis.prix_unitaire}
                    onChange={(e) => setNewDevis({...newDevis, prix_unitaire: e.target.value})}
                    className="form-input"
                    required
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Économies annuelles (€)</label>
                  <input
                    type="number"
                    value={newDevis.economies_annuelles}
                    onChange={(e) => setNewDevis({...newDevis, economies_annuelles: e.target.value})}
                    className="form-input"
                  />
                  <button
                    type="button"
                    onClick={calculateEstimatedSavings}
                    className="text-xs text-primary hover:text-primary-dark mt-1"
                  >
                    Calculer automatiquement
                  </button>
                </div>
              </div>

              {/* Price Preview */}
              {newDevis.nombre_panneaux && newDevis.prix_unitaire && (
                <div className="bg-gray-50 rounded-lg p-4">
                  <h3 className="font-medium text-gray-900 mb-2">Aperçu du devis</h3>
                  <div className="space-y-1 text-sm">
                    <div className="flex justify-between">
                      <span>Prix HT:</span>
                      <span className="font-medium">
                        {new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' })
                          .format(parseInt(newDevis.nombre_panneaux) * parseFloat(newDevis.prix_unitaire))}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span>TVA (20%):</span>
                      <span className="font-medium">
                        {new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' })
                          .format(parseInt(newDevis.nombre_panneaux) * parseFloat(newDevis.prix_unitaire) * 0.2)}
                      </span>
                    </div>
                    <div className="flex justify-between border-t pt-1 font-semibold">
                      <span>Prix TTC:</span>
                      <span>
                        {new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' })
                          .format(parseInt(newDevis.nombre_panneaux) * parseFloat(newDevis.prix_unitaire) * 1.2)}
                      </span>
                    </div>
                  </div>
                </div>
              )}

              <div className="form-group">
                <label className="form-label">Notes</label>
                <textarea
                  value={newDevis.notes}
                  onChange={(e) => setNewDevis({...newDevis, notes: e.target.value})}
                  className="form-input"
                  rows="3"
                  placeholder="Notes techniques, conditions particulières..."
                ></textarea>
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowAddForm(false)}
                  className="btn btn-secondary"
                >
                  Annuler
                </button>
                <button type="submit" className="btn btn-primary">
                  <Plus className="w-4 h-4" />
                  Créer le devis
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Devis;